import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { AlertTriangle, BellRing, ChevronDown, Loader2, Plus, Trash2 } from "lucide-react";
import { cn } from "../../../../utils/cn";
import { Card } from "../../../../components/ui/Card";
import { Skeleton } from "../../../../components/ui/Skeleton";
import { PendingBackendNotice } from "../../../../components/ui/PendingBackendNotice";
import { showToast } from "../../../../utils/showToast";
import { apiErrorMessage, apiErrorStatus } from "../../../../utils/apiErrorMessage";
import { getAiSettings, getWhatsappGroups, isEndpointMissing, saveAiSettings } from "./aiSettingsApi";
import { formatDateTime } from "../reports/format";
import { aiErrorMessage } from "./aiErrorMessage";
import { CONTROL_CLS, PRIMARY_BTN_CLS, SECONDARY_BTN_CLS } from "./agentSections/primitives";
import {
  HANDOFF_AREAS,
  HANDOFF_AREA_HELP,
  HANDOFF_AREA_LABELS,
  HANDOFF_CONTACTS_MAX,
  HANDOFF_NAME_MAX,
  contactsSignature,
  contactsToRows,
  emptyHandoffRow,
  handoffGroupOptions,
  normalizeHandoffGroup,
  rowsToContacts,
  validateHandoffRows,
} from "./handoffContacts";

// Avisos de derivación: a quién del courier se le avisa por WhatsApp cuando la
// IA deriva un caso de su área. Es configuración del COURIER (vale para todos
// los agentes y para la configuración base), por eso vive como tarjeta propia
// de la pestaña de agentes —igual que la cuenta de servicio— con su propio
// guardado, y no dentro del formulario base, que se atenúa con la IA propia
// apagada aunque los agentes sí deriven.
//
// El PUT es parcial: viaja solo `handoff_contacts` (lista completa, reemplaza la
// guardada) más el `mode` que exige el DTO, RELEÍDO justo antes de guardar —
// mismo criterio que el interruptor maestro: mandar uno cacheado pisaría el que
// el formulario base acaba de cambiar.
//
// Rol: el backend trata esta lista como parte de la config de la IA. Sin
// permiso de configuración, o con la IA leyendo pedidos que el rol no puede
// conceder (solo puede apagarla), el PUT se rechaza: la tarjeta queda de solo
// lectura y dice por qué (`readOnlyReason`, calculado por la página).
//
// Grupo del equipo (opcional): `handoff_group` viaja en el mismo PUT (null =
// sin grupo). Solo se manda si el GET trae la clave (aunque sea null): sin ella
// el backend aún no la conoce y la rechazaría. La lista de grupos sale de
// microservice-conversations y tiene su propio estado: si falla, la tarjeta
// sigue funcionando y se ofrece al menos el grupo guardado.

/**
 * @param {{readOnlyReason?: "role"|"orders"|null}} props
 */
export default function HandoffContactsCard({ readOnlyReason = null }) {
  const { t } = useTranslation();

  // loading → leyendo; missing → backend sin el campo (o sin la ruta);
  // error → falló de verdad; ready → editable (según el rol).
  const [state, setState] = useState("loading");
  const [loadError, setLoadError] = useState(null);
  const [rows, setRows] = useState([]);
  // Firma de lo GUARDADO: habilita "Guardar" solo si hay cambios.
  const [savedSignature, setSavedSignature] = useState("[]");
  const [rowErrors, setRowErrors] = useState({});
  const [formError, setFormError] = useState(null);
  const [saving, setSaving] = useState(false);
  // Grupo: soportado por el backend (el GET trae la clave), elegido, guardado
  // y la lista de grupos (loading | ready | error).
  const [groupSupported, setGroupSupported] = useState(false);
  const [group, setGroup] = useState(null);
  const [savedGroup, setSavedGroup] = useState(null);
  const [groups, setGroups] = useState([]);
  const [groupsState, setGroupsState] = useState("loading");

  // StrictMode monta → limpia → monta sobre el MISMO fiber: `alive` se re-fija
  // en el cuerpo del efecto (ver ServiceAccountCard).
  const aliveRef = useRef(true);
  useEffect(() => {
    aliveRef.current = true;
    return () => {
      aliveRef.current = false;
    };
  }, []);

  // `t` fuera de las deps del load: cambiar el idioma no puede recargar la
  // tarjeta y borrar lo que se estaba editando.
  const tRef = useRef(t);
  tRef.current = t;

  // Lista de grupos aparte: su error nunca tumba la tarjeta.
  const loadGroups = useCallback(async () => {
    setGroupsState("loading");
    try {
      const res = await getWhatsappGroups();
      if (!aliveRef.current) return;
      setGroups(Array.isArray(res?.groups) ? res.groups : []);
      setGroupsState("ready");
    } catch {
      if (!aliveRef.current) return;
      setGroups([]);
      setGroupsState("error");
    }
  }, []);

  const load = useCallback(async () => {
    setState("loading");
    setLoadError(null);
    loadGroups();
    try {
      const res = await getAiSettings();
      if (!aliveRef.current) return;
      const list = res?.settings?.handoff_contacts;
      // Backend sin el campo: no se ofrece editar (un PUT con la clave
      // desconocida lo rechazaría la validación del backend).
      if (!Array.isArray(list)) {
        setState("missing");
        return;
      }
      setRows(contactsToRows(list));
      setSavedSignature(contactsSignature(list));
      const supported = res.settings.handoff_group !== undefined;
      const current = supported ? normalizeHandoffGroup(res.settings.handoff_group) : null;
      setGroupSupported(supported);
      setGroup(current);
      setSavedGroup(current);
      setRowErrors({});
      setFormError(null);
      setState("ready");
    } catch (err) {
      if (!aliveRef.current) return;
      if (isEndpointMissing(err)) {
        setState("missing");
      } else {
        setLoadError(
          aiErrorMessage(err, tRef.current) ||
            tRef.current("No se pudieron cargar los avisos de derivación."),
        );
        setState("error");
      }
    }
  }, [loadGroups]);

  useEffect(() => {
    load();
  }, [load]);

  const readOnly = readOnlyReason === "role" || readOnlyReason === "orders";
  const disabled = readOnly || saving;

  const updateRow = (id, patch) => {
    setRows((prev) => prev.map((r) => (r.id === id ? { ...r, ...patch } : r)));
    // El error de ese campo se va apenas se corrige (no espera al guardado).
    setRowErrors((prev) => {
      if (!prev[id]) return prev;
      const next = { ...prev, [id]: { ...prev[id] } };
      Object.keys(patch).forEach((key) => delete next[id][key]);
      if (Object.keys(next[id]).length === 0) delete next[id];
      return next;
    });
    setFormError(null);
  };

  const addRow = () => {
    setRows((prev) => (prev.length >= HANDOFF_CONTACTS_MAX ? prev : [...prev, emptyHandoffRow()]));
    setFormError(null);
  };

  const removeRow = (id) => {
    setRows((prev) => prev.filter((r) => r.id !== id));
    setRowErrors((prev) => {
      if (!prev[id]) return prev;
      const next = { ...prev };
      delete next[id];
      return next;
    });
    setFormError(null);
  };

  const handleSave = useCallback(
    async (event) => {
      event.preventDefault();
      if (saving || readOnly) return;
      const { errors, tooMany } = validateHandoffRows(rows);
      if (tooMany) {
        setFormError(t("Puedes registrar hasta {{max}} contactos.", { max: HANDOFF_CONTACTS_MAX }));
        return;
      }
      if (Object.keys(errors).length > 0) {
        setRowErrors(errors);
        setFormError(t("Revisa los contactos marcados."));
        return;
      }
      setRowErrors({});
      setFormError(null);
      setSaving(true);
      const contacts = rowsToContacts(rows);
      try {
        // `mode` fresco: el DTO lo exige y el cacheado podría ser viejo.
        const fresh = await getAiSettings();
        const body = { mode: fresh?.settings?.mode, handoff_contacts: contacts };
        if (groupSupported) body.handoff_group = group ? { jid: group.jid, name: group.name } : null;
        const res = await saveAiSettings(body);
        if (!aliveRef.current) return;
        // Lo que el backend confirma (teléfonos en dígitos, sin duplicados) es
        // lo que queda en pantalla; sin el campo en la respuesta, lo enviado.
        const saved = Array.isArray(res?.settings?.handoff_contacts)
          ? res.settings.handoff_contacts
          : contacts;
        setRows(contactsToRows(saved));
        setSavedSignature(contactsSignature(saved));
        if (groupSupported) {
          const confirmed =
            res?.settings?.handoff_group !== undefined
              ? normalizeHandoffGroup(res.settings.handoff_group)
              : group;
          setGroup(confirmed);
          setSavedGroup(confirmed);
        }
        showToast(t("Avisos de derivación guardados."), "success");
      } catch (err) {
        if (!aliveRef.current) return;
        // Mismo orden que las acciones de agentes: un código tipado (403 de
        // rol) se traduce; si no, el mensaje de NEGOCIO del backend (el 400 de
        // validación dice qué campo falló); un 5xx no filtra su texto interno.
        const typedMessage = err?.code ? aiErrorMessage(err, t) : "";
        const status = apiErrorStatus(err);
        const serverMessage = status !== null && status < 500 ? apiErrorMessage(err, "") : "";
        setFormError(
          isEndpointMissing(err) && !serverMessage
            ? t("Esta opción está en construcción.")
            : typedMessage || serverMessage || t("No se pudieron guardar los avisos de derivación."),
        );
      } finally {
        if (aliveRef.current) setSaving(false);
      }
    },
    [group, groupSupported, readOnly, rows, saving, t],
  );

  const dirty =
    contactsSignature(rowsToContacts(rows)) !== savedSignature ||
    (groupSupported && (group?.jid ?? "") !== (savedGroup?.jid ?? ""));
  // Se mezcla el GUARDADO (no el elegido): con la lista caída, pasar a "Sin
  // grupo" no puede hacer desaparecer la opción de volver a él.
  const groupOptions = handoffGroupOptions(groups, savedGroup);
  // Sin soporte en el backend, o sin lista y sin grupo guardado, no hay nada
  // que elegir: select deshabilitado con el motivo.
  const groupUnavailable =
    !groupSupported || (groupsState === "error" && groupOptions.length === 0);
  const selectedGroupOption = group ? groupOptions.find((g) => g.jid === group.jid) : null;
  const groupOptionLabel = (g) =>
    g.lastMessageAt ? `${g.name} · ${formatDateTime(g.lastMessageAt)}` : g.name;
  const selectGroup = (jid) => {
    const option = groupOptions.find((g) => g.jid === jid);
    setGroup(option ? { jid: option.jid, name: option.name } : null);
    setFormError(null);
  };
  const atLimit = rows.length >= HANDOFF_CONTACTS_MAX;
  const describedBy = readOnly ? "ai-handoff-help ai-handoff-readonly" : "ai-handoff-help";

  return (
    <Card padding="md">
      <div className="flex items-start gap-3">
        <span
          className="inline-flex w-9 h-9 items-center justify-center rounded-lg bg-bg-muted text-fg-muted shrink-0"
          aria-hidden="true"
        >
          <BellRing className="w-4 h-4" />
        </span>
        <div className="min-w-0 flex-1 space-y-1">
          <h2 className="text-xs uppercase tracking-wide font-semibold text-fg-muted">
            {t("Avisos de derivación")}
          </h2>
          <p id="ai-handoff-help" className="text-[12px] md:text-[11px] text-fg-muted">
            {t(
              "Cuando la IA deriva un caso, cada contacto del área recibe un WhatsApp desde el número de notificaciones con el contacto, el motivo y el enlace al chat.",
            )}
          </p>
          {/* El motivo acompaña a los controles deshabilitados: sin
              formulario (cargando, backend viejo) no hay nada que explicar. */}
          {readOnly && state === "ready" && (
            <p id="ai-handoff-readonly" className="text-[12px] md:text-[11px] text-fg-muted">
              {readOnlyReason === "orders"
                ? t("La IA de la empresa lee pedidos y tu rol no puede conceder ese acceso: solo puedes apagarla.")
                : t("Tu rol no permite esta acción de la IA.")}
            </p>
          )}
        </div>
      </div>

      {state === "loading" && (
        <div className="mt-4 space-y-2" aria-hidden="true">
          <Skeleton className="h-3 w-48 rounded" />
          <Skeleton className="h-11 md:h-8 w-full rounded-md" />
        </div>
      )}

      {state === "missing" && (
        <PendingBackendNotice className="mt-4">{t("Esta opción está en construcción.")}</PendingBackendNotice>
      )}

      {state === "error" && (
        <div className="mt-4 flex items-center gap-2 text-xs text-status-error" role="alert">
          <AlertTriangle className="w-3.5 h-3.5 shrink-0" aria-hidden="true" />
          <span className="min-w-0">{loadError}</span>
          <button
            type="button"
            onClick={load}
            className="h-8 px-3 rounded-md text-xs font-medium text-brand-700 hover:bg-brand-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 transition-colors"
          >
            {t("Reintentar")}
          </button>
        </div>
      )}

      {state === "ready" && (
        <form className="mt-4 space-y-3" onSubmit={handleSave} noValidate>
          {/* Qué cubre cada área: sin esto "primera milla" es jerga. */}
          <ul className="space-y-0.5 text-[12px] md:text-[11px] text-fg-muted">
            {HANDOFF_AREAS.map((area) => (
              <li key={area}>
                <span className="font-medium text-fg">{t(HANDOFF_AREA_LABELS[area])}:</span>{" "}
                {t(HANDOFF_AREA_HELP[area])}
              </li>
            ))}
          </ul>

          {rows.length === 0 ? (
            <p className="text-xs text-fg-muted">
              {t("Sin contactos: al derivar no se avisa a nadie por WhatsApp.")}
            </p>
          ) : (
            <ul className="divide-y divide-border" aria-label={t("Contactos de derivación")}>
              {rows.map((row, index) => {
                const errors = rowErrors[row.id] || {};
                const n = index + 1;
                const nameErrId = `ai-handoff-${row.id}-name-err`;
                const phoneErrId = `ai-handoff-${row.id}-phone-err`;
                return (
                  <li key={row.id} className="py-2.5 first:pt-0 last:pb-0 space-y-1">
                    <div className="grid grid-cols-1 gap-2 sm:grid-cols-[10rem_minmax(0,1fr)_11rem_auto] sm:items-center">
                      <div className="relative">
                        <select
                          value={row.area}
                          onChange={(e) => updateRow(row.id, { area: e.target.value })}
                          disabled={disabled}
                          aria-label={t("Área del contacto {{n}}", { n })}
                          aria-describedby={describedBy}
                          className={cn(CONTROL_CLS, "appearance-none !pr-8")}
                        >
                          {HANDOFF_AREAS.map((area) => (
                            <option key={area} value={area}>
                              {t(HANDOFF_AREA_LABELS[area])}
                            </option>
                          ))}
                        </select>
                        <ChevronDown
                          className="absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-fg-muted pointer-events-none"
                          aria-hidden="true"
                        />
                      </div>
                      <input
                        type="text"
                        value={row.name}
                        onChange={(e) => updateRow(row.id, { name: e.target.value })}
                        disabled={disabled}
                        maxLength={HANDOFF_NAME_MAX}
                        placeholder={t("Nombre")}
                        aria-label={t("Nombre del contacto {{n}}", { n })}
                        aria-invalid={!!errors.name}
                        aria-describedby={errors.name ? nameErrId : describedBy}
                        className={CONTROL_CLS}
                      />
                      <input
                        type="tel"
                        inputMode="tel"
                        autoComplete="off"
                        value={row.phone}
                        onChange={(e) => updateRow(row.id, { phone: e.target.value })}
                        disabled={disabled}
                        placeholder="+56912345678"
                        aria-label={t("Teléfono del contacto {{n}}", { n })}
                        aria-invalid={!!errors.phone}
                        aria-describedby={errors.phone ? phoneErrId : describedBy}
                        className={CONTROL_CLS}
                      />
                      <button
                        type="button"
                        onClick={() => removeRow(row.id)}
                        disabled={disabled}
                        aria-label={t("Quitar contacto {{n}}", { n })}
                        className={cn(
                          SECONDARY_BTN_CLS,
                          "w-11 md:w-8 px-0 border-transparent text-fg-muted hover:text-status-error hover:bg-status-error-soft",
                        )}
                      >
                        <Trash2 className="w-3.5 h-3.5" aria-hidden="true" />
                      </button>
                    </div>
                    {errors.name && (
                      <p id={nameErrId} className="text-[12px] md:text-[11px] text-status-error">
                        {t(errors.name)}
                      </p>
                    )}
                    {errors.phone && (
                      <p id={phoneErrId} className="text-[12px] md:text-[11px] text-status-error">
                        {t(errors.phone)}
                      </p>
                    )}
                  </li>
                );
              })}
            </ul>
          )}

          <div className="space-y-1 pt-3 border-t border-border">
            <label htmlFor="ai-handoff-group" className="block text-xs font-medium text-fg">
              {t("Grupo del equipo (opcional)")}
            </label>
            <p id="ai-handoff-group-help" className="text-[12px] md:text-[11px] text-fg-muted">
              {t(
                "Con un grupo, los avisos y las consultas de la IA llegan al grupo y cualquiera ahí puede responder citando el mensaje o escribiendo su código.",
              )}
            </p>
            <div className="relative sm:max-w-md">
              <select
                id="ai-handoff-group"
                value={group?.jid ?? ""}
                onChange={(e) => selectGroup(e.target.value)}
                disabled={disabled || groupUnavailable || groupsState === "loading"}
                aria-describedby={
                  readOnly
                    ? "ai-handoff-group-help ai-handoff-readonly"
                    : "ai-handoff-group-help ai-handoff-group-status"
                }
                className={cn(CONTROL_CLS, "appearance-none !pr-8")}
              >
                <option value="">{t("Sin grupo — enviar mensajes directos")}</option>
                {groupOptions.map((g) => (
                  <option key={g.jid} value={g.jid}>
                    {groupOptionLabel(g)}
                  </option>
                ))}
              </select>
              <ChevronDown
                className="absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-fg-muted pointer-events-none"
                aria-hidden="true"
              />
            </div>
            <p id="ai-handoff-group-status" className="text-[12px] md:text-[11px] text-fg-muted">
              {groupsState === "loading" && groupSupported
                ? t("Cargando grupos…")
                : groupUnavailable
                  ? t("La lista de grupos aún no está disponible.")
                  : groupsState === "error"
                    ? t("No se pudo cargar la lista de grupos; se muestra el grupo guardado.")
                    : selectedGroupOption?.lastMessageAt
                      ? t("Última actividad: {{date}}", {
                          date: formatDateTime(selectedGroupOption.lastMessageAt),
                        })
                      : null}
            </p>
          </div>

          {formError && (
            <p role="alert" className="flex items-start gap-1.5 text-xs text-status-error">
              <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" aria-hidden="true" />
              <span className="min-w-0 break-words">{formError}</span>
            </p>
          )}

          <div className="flex items-center gap-2 flex-wrap">
            <button
              type="button"
              onClick={addRow}
              disabled={disabled || atLimit}
              aria-describedby={describedBy}
              className={SECONDARY_BTN_CLS}
            >
              <Plus className="w-3.5 h-3.5" aria-hidden="true" />
              {t("Agregar contacto")}
            </button>
            <button
              type="submit"
              disabled={disabled || !dirty}
              aria-describedby={describedBy}
              className={PRIMARY_BTN_CLS}
            >
              {saving && <Loader2 className="w-3.5 h-3.5 animate-spin" aria-hidden="true" />}
              {saving ? t("Guardando…") : t("Guardar avisos")}
            </button>
            {atLimit && (
              <span className="text-[12px] md:text-[11px] text-fg-muted">
                {t("Llegaste al máximo de {{max}} contactos.", { max: HANDOFF_CONTACTS_MAX })}
              </span>
            )}
          </div>
        </form>
      )}
    </Card>
  );
}
